// Per-mech style sheets for the procedural cutout rig: proportions, palettes,
// gait and persona tuning. Everything the rig, poses and painters need to make
// VOLT, BASTION and WRAITH read as three different machines at gameplay size.
//
// All lengths are pixels in the 128×128 frame cell (gameplay scale). The cell's
// foot anchor is FOOT_ANCHOR (64,124): the ground line every grounded frame
// stands on.

import { COLORS } from '../../config/palette';
import type { MechId } from '../../core/types';

export type Pt = readonly [number, number];

/** Three-tone cel ramp plus a crisp edge highlight. */
export interface Ramp {
  shadow: number;
  base: number;
  light: number;
  edge: number;
}

/**
 * Materials. armor = primary plating, plate = secondary/darker plating,
 * accent = per-mech trim paint, frame = dark structure (waist, neck, inner limbs),
 * metal = bare joints and pistons.
 */
export type MatKey = 'armor' | 'plate' | 'accent' | 'frame' | 'metal';

export interface MechPalette {
  mats: Record<MatKey, Ramp>;
  /** Unlit emissive glass (visor, reactor lens, light strips). */
  glass: number;
  /** Reactor colour baked (dimmed) into the body sheet; the glow sheet itself is white. */
  reactor: number;
}

/** Run-cycle gait. x values are ankle offsets from the anchor, lift = sole height above the ground. */
export interface RunGait {
  /** Fraction of the cycle each foot is planted (< 0.5 gives two flight phases). */
  duty: number;
  /** Ankle x at contact / toe-off. */
  front: number;
  back: number;
  /** Hip lowered relative to the idle stance. */
  drop: number;
  /** Vertical bob amplitude (px). */
  bob: number;
  /** Cycle phase of the lowest hip (mid-stance compression). */
  lowPhase: number;
  lean: number;
  leanOsc: number;
  pelvisOsc: number;
  hipX: number;
  /** Foot pitch at toe-off and at contact (positive = toe down). */
  toeOff: number;
  contact: number;
  /** Swing path control points: [ankle x, lift, foot angle]. */
  kick: readonly [number, number, number];
  pass: readonly [number, number, number];
  drive: readonly [number, number, number];
  reach: readonly [number, number, number];
}

export interface MechStyle {
  id: MechId;
  pal: MechPalette;

  // ── Skeleton ──
  thigh: number;
  shin: number;
  /** Ankle joint height above the sole when the foot is flat. */
  ankleH: number;
  /** Toe tip / heel distance from the ankle along the sole. */
  toe: number;
  heel: number;
  /** Hip joints sit at (±hipSpread, hipJointY) in the pelvis frame (front +, rear −). */
  hipSpread: number;
  hipJointY: number;
  /** Pelvis-centre height above the ground in the neutral stance. */
  standHip: number;
  /** Neutral stance: ankle x offsets of the front and rear foot, torso lean. */
  stanceF: number;
  stanceR: number;
  lean: number;
  /** Landmarks in the torso frame (origin = pelvis centre, −y = up). */
  torso: {
    neck: Pt;
    shoulderF: Pt;
    shoulderR: Pt;
    chest: Pt;
    back: Pt;
  };
  /** Head centre in the head frame (origin = neck pivot). */
  headCenter: Pt;

  // ── Motion persona ──
  run: RunGait;
  /** Idle breathing (px), torso sway (rad), floating-part bob (px). */
  idleBob: number;
  idleSway: number;
  floatBob: number;
  /** Shoot sheet: arm pull-back (px) and muzzle rise (rad) per frame. */
  recoil: readonly number[];
  lift: readonly number[];

  // ── Arms (separate runtime parts) ──
  arm: {
    upperLen: number;
    foreLen: number;
    /** Multiply tint for the far arm. */
    rearTint: number;
  };
}

const ramp = (shadow: number, base: number, light: number, edge: number): Ramp => ({ shadow, base, light, edge });

/** VOLT — slim racing frame, angular light-steel plates, cobalt trim, cyan reactor. Forward-leaning. */
const VOLT: MechStyle = {
  id: 'volt',
  pal: {
    mats: {
      armor: ramp(0x51617a, 0x8b9cb3, 0xbfcddd, 0xe9f1f9),
      plate: ramp(0x313b4b, 0x506075, 0x76879e, 0xa7b6c9),
      accent: ramp(0x18456d, 0x2a6fae, 0x55a0df, 0xa3d6ff),
      frame: ramp(0x12161d, 0x232a35, 0x384353, 0x5a6779),
      metal: ramp(0x38414f, 0x5d697b, 0x929fb3, 0xd2dbe6),
    },
    glass: 0x0d2a33,
    reactor: COLORS.volt,
  },
  thigh: 21,
  shin: 23,
  ankleH: 5,
  toe: 12,
  heel: 6,
  hipSpread: 2,
  hipJointY: 2,
  standHip: 45,
  stanceF: 9,
  stanceR: -12,
  lean: 0.11,
  torso: {
    neck: [1.5, -38],
    shoulderF: [-0.5, -31.5],
    shoulderR: [-3.5, -33],
    chest: [4.5, -25],
    back: [-16, -27],
  },
  headCenter: [2, -7.5],
  run: {
    duty: 0.36,
    front: 15,
    back: -19,
    drop: 3,
    bob: 2.2,
    lowPhase: 0.13,
    lean: 0.27,
    leanOsc: 0.025,
    pelvisOsc: 0.05,
    hipX: 2,
    toeOff: 0.55,
    contact: -0.14,
    kick: [-22, 19, 1.15],
    pass: [-6, 19, 0.8],
    drive: [11, 15, 0.32],
    reach: [18, 6, -0.05],
  },
  idleBob: 0.9,
  idleSway: 0.016,
  floatBob: 0,
  recoil: [0, 7, 5, 3, 1, 0],
  lift: [0, -0.18, -0.12, -0.06, -0.02, 0],
  arm: { upperLen: 19, foreLen: 21, rearTint: 0x8892a6 },
};

/** BASTION — broad torso block, heavy warm-steel plating, ochre hazard trim, amber reactor. Low and wide. */
const BASTION: MechStyle = {
  id: 'bastion',
  pal: {
    mats: {
      armor: ramp(0x4f4943, 0x7f776c, 0xada395, 0xdcd3c4),
      plate: ramp(0x33302c, 0x534e48, 0x766f66, 0x9d9588),
      accent: ramp(0x774a0f, 0xbb7a1b, 0xe5a43f, 0xffd486),
      frame: ramp(0x151619, 0x27282d, 0x3e4048, 0x5d5f69),
      metal: ramp(0x3d3a36, 0x66615a, 0x9a948a, 0xd6cfc3),
    },
    glass: 0x33210a,
    reactor: COLORS.bastion,
  },
  thigh: 19,
  shin: 20,
  ankleH: 7,
  toe: 14,
  heel: 10,
  hipSpread: 4,
  hipJointY: 3,
  standHip: 42,
  stanceF: 13,
  stanceR: -15,
  lean: 0.03,
  torso: {
    neck: [3, -39],
    shoulderF: [1, -31],
    shoulderR: [-4.5, -32.5],
    chest: [9, -23],
    back: [-24, -26],
  },
  headCenter: [2.5, -5],
  run: {
    duty: 0.44,
    front: 13,
    back: -15,
    drop: 2,
    bob: 2.6,
    lowPhase: 0.15,
    lean: 0.11,
    leanOsc: 0.02,
    pelvisOsc: 0.035,
    hipX: 1,
    toeOff: 0.35,
    contact: -0.08,
    kick: [-17, 11, 0.8],
    pass: [-4, 12, 0.5],
    drive: [9, 10, 0.18],
    reach: [15, 4, -0.04],
  },
  idleBob: 1.1,
  idleSway: 0.01,
  floatBob: 0,
  recoil: [0, 9, 6.5, 4, 1.5, 0],
  lift: [0, -0.14, -0.1, -0.05, -0.02, 0],
  arm: { upperLen: 19, foreLen: 21, rearTint: 0x8f8a84 },
};

/** WRAITH — narrow frame on long legs, floating fins and halo, dark violet-steel, violet reactor. */
const WRAITH: MechStyle = {
  id: 'wraith',
  pal: {
    mats: {
      armor: ramp(0x35364f, 0x5c5e83, 0x8e90b8, 0xc4c5e8),
      plate: ramp(0x23243a, 0x3b3c5a, 0x57597e, 0x8183ab),
      accent: ramp(0x3a1d63, 0x6a3ea6, 0x9a6fda, 0xd2b8ff),
      frame: ramp(0x101118, 0x1e1f2e, 0x32344b, 0x4d4f6c),
      metal: ramp(0x34334a, 0x55546f, 0x86859f, 0xc0bfd8),
    },
    glass: 0x1f0f33,
    reactor: COLORS.wraith,
  },
  thigh: 24,
  shin: 26,
  ankleH: 6,
  toe: 11,
  heel: 5,
  hipSpread: 1.5,
  hipJointY: 2,
  standHip: 55,
  stanceF: 8,
  stanceR: -9,
  lean: 0.07,
  torso: {
    neck: [1, -30],
    shoulderF: [-0.5, -24.5],
    shoulderR: [-3, -25.5],
    chest: [2.5, -18],
    back: [-10, -20],
  },
  headCenter: [2.5, -7.5],
  run: {
    duty: 0.33,
    front: 17,
    back: -20,
    drop: 3,
    bob: 1.7,
    lowPhase: 0.12,
    lean: 0.21,
    leanOsc: 0.02,
    pelvisOsc: 0.05,
    hipX: 1,
    toeOff: 0.6,
    contact: -0.1,
    kick: [-24, 23, 1.2],
    pass: [-6, 23, 0.85],
    drive: [13, 18, 0.35],
    reach: [20, 7, 0],
  },
  idleBob: 0.8,
  idleSway: 0.02,
  floatBob: 1.8,
  recoil: [0, 6, 4, 2.5, 1, 0],
  lift: [0, -0.22, -0.14, -0.07, -0.02, 0],
  arm: { upperLen: 20, foreLen: 22, rearTint: 0x8a86a8 },
};

export const MECH_STYLES: Readonly<Record<MechId, MechStyle>> = { volt: VOLT, bastion: BASTION, wraith: WRAITH };
